import { useCallback, useMemo, useState } from "react";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";
import TabBox from "../../Components/Tabbox/TabBox";
import StepAddOrderBox from "./AddOrder/StepAddOrderBox";
import StepOrderBook from "./StepOrderBook";
import ErrorsTable from "../../Tables/Order/ErrorsTable/ErrorsTable";
import FailedOrdersTable from "../../Tables/Order/FailedOrdersTable/FailedOrdersTable";
import PreviewStepOrderTable from "../../Tables/Step/PreviewStepOrderTable";
import StepOrderTable from "../../Tables/Step/StepOrder/StepOrderTable";
import StepTradeTable from "../../Tables/Step/StepTrade/StepTradeTable";
import {
  getStepErrorsTable,
  getStepFailedOrderTable,
  previewStepFailedOrderResend,
  previewStepFailedOrderResendBulk,
  resendStepFailedOrder,
  resendStepFailedOrdersBulk,
} from "../../Requests/stepRequests";
import StepPortfoTable from "../../Tables/Step/StepPortfoTable";
import StepFundOrderTable from "../../Tables/Step/FundOrder/FundOrderTable";
import EditOrder from "./EditOrder";
import SearchParam, { StepFilterMode } from "./SearchParam";
import { IStepTablesSearchParam } from "../../Interfaces/stepInterfaces";
import { IFundOrderTableSearchParams } from "../../Interfaces/manualInterfaces";
import AggregationTable from "../../Tables/Step/AggregationTable";

enum StepOrderTablesTabIndex {
  OrderTable = 1,
  TradeTable,
  TradeAggregateTable,
  ErrorsTable,
  AggregationTable,
  FundOrderTable,
  FailedOrdersTable,
}

const Step = () => {
  const [tabIndex, setTabIndex] = useState(StepOrderTablesTabIndex.OrderTable);

  const [tablesSearchParam, setOrderTradeSearchParam] =
    useState<IStepTablesSearchParam>({} as IStepTablesSearchParam);
  const [fundInstrumentId, setFundInstrumentId] = useState<string | null>(null);
  const fundOrderSearchParam = useMemo<IFundOrderTableSearchParams>(() => ({
    portfolioGroupId: tablesSearchParam.portfolioGroupId,
    sahraUserId: tablesSearchParam.sahraUserid,
    instrumentId: fundInstrumentId,
  }), [tablesSearchParam.portfolioGroupId, tablesSearchParam.sahraUserid, fundInstrumentId]);

  const { errorTabCount, errorTableRefreshFlag, errorTableAfterFetchHandler } =
    useErrorTabCount(
      "step",
      tabIndex,
      StepOrderTablesTabIndex.ErrorsTable,
      getStepErrorsTable,
    );

  const onTablesSearchParamChange = useCallback((name: string, value: any) => {
    setOrderTradeSearchParam((previous) => ({ ...previous, [name]: value }));
  }, []);

  const onFundOrderSearchParamChange = useCallback((name: string, value: any) => {
    const normalizedValue = value === "" || value == null ? null : value;
    if (name === "instrumentId") {
      setFundInstrumentId(normalizedValue);
    } else {
      onTablesSearchParamChange(name === "sahraUserId" ? "sahraUserid" : name, normalizedValue);
    }
  }, [onTablesSearchParamChange]);

  const tablesTabsChildren = [
    { index: StepOrderTablesTabIndex.OrderTable, title: "سفارشات" },
    { index: StepOrderTablesTabIndex.TradeTable, title: "معاملات" },
    { index: StepOrderTablesTabIndex.AggregationTable, title: "تجمیعی" },
    { index: StepOrderTablesTabIndex.FundOrderTable, title: "معاملات صندوق" },
    {
      index: StepOrderTablesTabIndex.FailedOrdersTable,
      title: "سفارش‌های ناموفق",
    },
    {
      index: StepOrderTablesTabIndex.ErrorsTable,
      title: "خطا",
      count: errorTabCount,
    },
  ];

  const getTabIndexTable = () => {
    switch (tabIndex) {
      case StepOrderTablesTabIndex.OrderTable:
        return <StepOrderTable searchParam={tablesSearchParam} />;

      case StepOrderTablesTabIndex.TradeTable:
        return <StepTradeTable searchParam={tablesSearchParam} />;

      case StepOrderTablesTabIndex.AggregationTable:
        return <AggregationTable searchParam={tablesSearchParam} />;

      case StepOrderTablesTabIndex.FundOrderTable:
        return <StepFundOrderTable searchParam={fundOrderSearchParam} />;

      case StepOrderTablesTabIndex.ErrorsTable:
        return (
          <ErrorsTable
            getData={getStepErrorsTable}
            searchParams={tablesSearchParam}
            onFetchComplete={errorTableAfterFetchHandler}
            refreshFlag={errorTableRefreshFlag}
          />
        );

      case StepOrderTablesTabIndex.FailedOrdersTable:
        return (
          <FailedOrdersTable
            getData={getStepFailedOrderTable}
            previewResend={previewStepFailedOrderResend}
            resendOrder={resendStepFailedOrder}
            renderPreviewRows={(rows) => <PreviewStepOrderTable values={rows} />}
            previewResendBulk={previewStepFailedOrderResendBulk}
            resendBulk={resendStepFailedOrdersBulk}
            searchParams={tablesSearchParam}
          />
        );
    }
  };

  const getFilterMode = (): StepFilterMode => {
    switch (tabIndex) {
      case StepOrderTablesTabIndex.FundOrderTable:
        return "fund";
      case StepOrderTablesTabIndex.TradeTable:
      case StepOrderTablesTabIndex.TradeAggregateTable:
        return "trade";
      case StepOrderTablesTabIndex.AggregationTable:
        return "aggregation";
      case StepOrderTablesTabIndex.ErrorsTable:
        return "errors";
      case StepOrderTablesTabIndex.FailedOrdersTable:
        return "failedOrders";
      default:
        return "order";
    }
  };

  return (
    <div className="flex flex-row justify-start items-start gap-5 w-full">
      <div className="flex flex-col justify-start items-start gap-5 mr-3">
        <StepAddOrderBox />
        <StepPortfoTable />
      </div>

      <div className="flex flex-col justify-start items-start">
        <div className="flex flex-row justify-start items-start w-full">
          <StepOrderBook />
        </div>
        <EditOrder />
        <div className="w-full border-t-2 border-gray-300 mt-3 pt-5">
          <div className="max-w-min m-auto">
            <div className="w-full flex flex-row justify-start items-center gap-3">
              <div className="max-w-min shrink-0">
                <TabBox
                  activeTab={tabIndex}
                  children={tablesTabsChildren}
                  onChange={(name, index) => setTabIndex(index)}
                />
              </div>
              <SearchParam
                onChange={onTablesSearchParamChange}
                searchParam={tablesSearchParam}
                mode={getFilterMode()}
                fundSearchParam={fundOrderSearchParam}
                onFundChange={onFundOrderSearchParamChange}
              />
            </div>
            <div className="w-full">{getTabIndexTable()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step;
